/**
 * Reset Manager - Gestion centralisée de la réinitialisation avant chargement d'un nouveau GEDCOM
 *
 * Ce module centralise toutes les opérations de nettoyage nécessaires avant le chargement
 * d'un nouveau fichier GEDCOM pour éviter les fuites d'état entre les fichiers.
 */

// Logger
import { logger } from './debug/logger.js';

// Stores principaux
import gedcomDataStore from '../gedcom/stores/gedcomDataStore.js';
import { invalidate as invalidateFamilyTreeAdapter } from '../tabs/familyTree/familyTreeAdapter.js';
import statisticsStore from '../tabs/statistics/statisticsStore.js';
import timelineEventsStore from '../tabs/timeline/timelineEventsStore.js';
import familyTownsStore from '../tabs/familyMap/stores/familyTownsStore.js';
import rootPersonStore from '../common/stores/rootPersonStore.js';
import configStore from '../tabs/fanChart/fanConfigStore.js';
import timelineFiltersStore from '../tabs/timeline/timelineFiltersStore.js';
import uiStore from '../common/stores/uiStore.js';
import timelineStore from '../tabs/timeline/timelineStore.js';

// Managers
// FanChartManager sera importé dynamiquement pour le lazy loading
import moduleCache from './moduleCache.js';

/**
 * Fonction principale de réinitialisation complète
 * @returns {Promise<void>}
 */
export async function resetBeforeNewGedcom() {
    logger.gedcom.info('🔄 Réinitialisation avant nouveau GEDCOM');

    try {
        const startTime = performance.now();

        // 1. STORES DE DONNÉES (priorité haute - ordre important)
        await resetDataStores();

        // 2. STORES UI ET CONFIGURATION
        await resetUIStores();

        // 3. GESTIONNAIRES ET MANAGERS
        await resetManagers();

        // 4. CACHES ET MODULES
        await resetCaches();

        // 5. ÉVÉNEMENTS ET RÉACTIONS
        resetEventSubscriptions();

        // 6. NETTOYAGE DOM
        resetDOMElements();

        const duration = Math.round(performance.now() - startTime);
        logger.gedcom.perf(`✅ Réinitialisation terminée en ${duration}ms`);

    } catch (error) {
        logger.gedcom.error('❌ Erreur pendant la réinitialisation:', error);
        throw error;
    }
}

/**
 * Phase 1: Réinitialisation des stores de données
 */
async function resetDataStores() {
    logger.gedcom.debug('📊 Reset des stores de données');

    try {
        // Store principal GEDCOM - CRITIQUE
        gedcomDataStore.clearAllState();

        // Stores spécialisés
        invalidateFamilyTreeAdapter();
        statisticsStore.resetStatistics();
        timelineEventsStore.clearEvents();
        timelineStore.clearTimeline();

        // Stores géographiques
        familyTownsStore.clearAllTowns();

    } catch (error) {
        logger.gedcom.error('❌ Erreur dans resetDataStores:', error);
        throw error;
    }
}

/**
 * Phase 2: Réinitialisation des stores UI et configuration
 */
async function resetUIStores() {
    logger.ui.debug('🎨 Reset des stores UI');

    try {
        // Store personne racine
        rootPersonStore.resetHistory();
        if (rootPersonStore.root) {
            rootPersonStore.setRoot(null, { skipDraw: true });
        }

        // Stores UI et filtres
        timelineFiltersStore.resetFilters();

        // Note: fanConfigStore n'est pas réinitialisé ici car il contient
        // des paramètres de configuration qui doivent persister entre les fichiers
        // et le gedcomFileName est défini après le reset

        uiStore.resetToDefaults();

    } catch (error) {
        logger.ui.error('❌ Erreur dans resetUIStores:', error);
        throw error;
    }
}

/**
 * Phase 3: Réinitialisation des gestionnaires et managers
 */
async function resetManagers() {
    logger.gedcom.debug('⚙️ Reset des managers');

    try {
        // Fan chart manager (lazy loading)
        try {
            // Ne charger FanChartManager que s'il est déjà utilisé
            if (window.isFanChartInitialized) {
                const { FanChartManager } = await import('../tabs/fanChart/fanChartManager.js');
                FanChartManager.reset();
                logger.fan.debug('FanChartManager reset');
            }
        } catch (error) {
            logger.fan.debug('FanChartManager non chargé:', error.message);
        }

        // Carte Google Maps (chargement paresseux)
        try {
            const { googleMapsStore } = await import('../tabs/familyMap/stores/googleMapsStore.js');
            googleMapsStore.clearMap();
            logger.map.debug('Google Maps store cleared');
        } catch (error) {
            logger.map.debug('Module carte non chargé:', error.message);
        }

        // TODO: Ajouter d'autres managers
        // await TimelineManager.reset();
        // layerManager.resetAllLayers();
        // globalOffcanvasManager.closeAll();

    } catch (error) {
        logger.gedcom.error('❌ Erreur dans resetManagers:', error);
        throw error;
    }
}

/**
 * Phase 4: Réinitialisation des caches
 */
async function resetCaches() {
    logger.cache.debug('💾 Reset des caches');

    try {
        // Cache des modules
        moduleCache.clear();

        // Caches géographiques additionnels
        familyTownsStore.clearAllCaches();

        // TODO: Ajouter d'autres caches si nécessaire
        // geoCache.clear();
        // imageCache.clear();

    } catch (error) {
        logger.cache.error('❌ Erreur dans resetCaches:', error);
        throw error;
    }
}

/**
 * Phase 5: Réinitialisation des événements et réactions
 */
function resetEventSubscriptions() {
    try {
        // Les réactions MobX sont déjà nettoyées dans gedcomDataStore.clearAllState()
        // Mais on pourrait ajouter des vérifications supplémentaires ici

        // TODO: Ajouter nettoyage d'autres event listeners si nécessaire
        // storeEvents.removeAllListeners();
        // intersectionObserver?.disconnect();

        logger.events.debug('📡 Event subscriptions reset');

    } catch (error) {
        logger.events.debug('Erreur dans resetEventSubscriptions (non critique):', error);
        // Ne pas relancer l'erreur car ce n'est pas critique
    }
}

/**
 * Phase 6: Nettoyage des éléments DOM
 */
function resetDOMElements() {
    try {
        // Nettoyage TomSelect
        const tomSelect = rootPersonStore.tomSelect;
        if (tomSelect) {
            tomSelect.clearOptions();
            tomSelect.clear();
        }

        // Reset du sélecteur d'individus
        const individualSelect = document.getElementById("individual-select");
        if (individualSelect) {
            individualSelect.innerHTML = "";
            const placeholderOption = new Option("", "", true, true);
            placeholderOption.disabled = true;
            individualSelect.appendChild(placeholderOption);
        }

        // Désactivation des éléments UI
        const elementsToDisable = [
            "#download-menu",
            "#fanParametersDisplay",
            "#treeParametersDisplay"
        ];
        elementsToDisable.forEach(selector => {
            const el = document.querySelector(selector);
            if (el) {
                el.disabled = true;
            }
        });

        // Suppression des event listeners temporaires
        document.querySelectorAll(".parameter").forEach(el => {
            if (el && typeof configStore.handleSettingChange === 'function') {
                el.removeEventListener("change", configStore.handleSettingChange);
            }
        });

        logger.ui.debug('🏠 DOM elements reset');

    } catch (error) {
        logger.ui.debug('Erreur dans resetDOMElements (non critique):', error);
        // Ne pas relancer l'erreur car ce n'est pas critique
    }
}

/**
 * Fonction utilitaire pour vérifier si une réinitialisation est nécessaire
 * @returns {boolean}
 */
export function shouldReset() {
    return gedcomDataStore.getFileUploaded() || 
           gedcomDataStore.individualsCache.size > 0 ||
           rootPersonStore.root !== null;
}

/**
 * Export de toutes les fonctions pour usage séparé si nécessaire
 */
export {
    resetDataStores,
    resetUIStores,
    resetManagers,
    resetCaches,
    resetEventSubscriptions,
    resetDOMElements
};